import React from 'react';
import { BaseEdge, EdgeProps, getBezierPath } from 'reactflow';

import { getEdgeColor } from '../dag/styles';
import { useDagContext } from '../dbt-dag';
import { useInternalDagContext } from '../providers/InternalDagProvider';

const X_OFFSET = 4;
const Y_OFFSET = 8;

type BidirectionalEdgeComponentProps = EdgeProps<any>;

export const BidirectionalEdgeComponentImpl: React.ComponentType<
  BidirectionalEdgeComponentProps
> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  data,
}) => {
  const defaultData = { hasArrowhead: true, strokeWidth: 1, ...data };
  const {
    highlight: isHighlighted,
    isHidden,
    dbtCloudProject,
    isError,
    targetProject,
  } = defaultData;
  const [highPath] = getBezierPath({
    sourceX: sourceX - X_OFFSET,
    sourceY: sourceY - Y_OFFSET,
    sourcePosition,
    targetX,
    targetY: targetY - Y_OFFSET,
    targetPosition,
  });
  const [lowPath] = getBezierPath({
    sourceX: targetX + X_OFFSET,
    sourceY: targetY + Y_OFFSET,
    sourcePosition: targetPosition,
    targetX: sourceX - X_OFFSET,
    targetY: sourceY + Y_OFFSET,
    targetPosition: sourcePosition,
  });
  const { grain = 'project', activeDbtCloudProject } = useInternalDagContext();
  const { selectedNode } = useDagContext();

  const opacity = isHidden ? 0.2 : !isHighlighted && selectedNode ? 0.3 : 1;
  const targetDbtCloudProject = targetProject?.dbtCloudProject;
  const isExternal =
    ['project', 'column'].includes(grain) &&
    !!activeDbtCloudProject &&
    ((!!dbtCloudProject && activeDbtCloudProject !== dbtCloudProject) ||
      (!!targetDbtCloudProject && activeDbtCloudProject !== targetDbtCloudProject));

  return (
    <>
      <style>
        {`
        .react-flow__edge-interaction {
          pointer-events: none;
        }

        g {
          pointer-events: none;
        }

        svg {
          pointer-events: none;
        }
        `}
      </style>
      <BaseEdge
        style={{
          strokeWidth: 1,
          stroke: getEdgeColor({ isHighlighted, isError }),
          strokeDasharray: isExternal ? '5 4' : undefined,
          strokeLinecap: isExternal ? 'round' : undefined,
          pointerEvents: 'none',
          ...style,
          opacity,
        }}
        id={id}
        path={highPath}
        markerEnd={markerEnd}
      />
      <BaseEdge
        style={{
          strokeWidth: 1,
          stroke: getEdgeColor({ isHighlighted, isError }),
          strokeDasharray: isExternal ? '5 4' : undefined,
          strokeLinecap: isExternal ? 'round' : undefined,
          pointerEvents: 'none',
          ...style,
          opacity,
        }}
        id={id}
        path={lowPath}
        markerEnd={markerEnd}
      />
    </>
  );
};

const BidirectionalEdgeDisplay = React.memo(BidirectionalEdgeComponentImpl);

export const BidirectionalEdgeComponent: React.ComponentType<
  BidirectionalEdgeComponentProps
> = (props) => {
  return <BidirectionalEdgeDisplay {...props} />;
};
